import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ShepherdService } from 'angular-shepherd';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { DataService } from '../helpers/data.service';
import { WorkspaceService } from '../modules/setup/workspace/workspace.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

export interface OnboardingStepConfig {
  id: string;
  route: string;
  elementId: string;
  title: string;
  text: string;
  progress: number;
  itemName: string;
}

const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  {
    id: 'step-country',
    route: '/setup/countries',
    elementId: '#onboarding-add-country',
    title: 'Let\'s set up your location',
    text: 'First, create a country for your workspace.',
    progress: 0,
    itemName: 'Country'
  },
  {
    id: 'step-city',
    route: '/setup/cities',
    elementId: '#onboarding-add-city',
    title: 'Create your first city',
    text: 'Now let\'s add a city for your workspace.',
    progress: 10,
    itemName: 'City'
  },
  {
    id: 'step-company',
    route: '/setup/company',
    elementId: '#onboarding-add-company',
    title: 'Create your company',
    text: 'Let\'s add the company that will be used throughout your workspace.',
    progress: 20,
    itemName: 'Company'
  },
  {
    id: 'step-station',
    route: '/setup/station',
    elementId: '#onboarding-add-station',
    title: 'Set up your first station',
    text: 'Create a station under your company.',
    progress: 30,
    itemName: 'Station'
  },
  {
    id: 'step-department',
    route: '/setup/department',
    elementId: '#onboarding-add-department',
    title: 'Create your first department',
    text: 'Departments help organize your employees.',
    progress: 40,
    itemName: 'Department'
  },
  {
    id: 'step-designation',
    route: '/setup/designation',
    elementId: '#onboarding-add-designation',
    title: 'Create a designation',
    text: 'Add the designation that will be assigned to your employees.',
    progress: 50,
    itemName: 'Designation'
  },
  {
    id: 'step-role',
    route: '/setup/roles',
    elementId: '#onboarding-add-role',
    title: 'Create your first role',
    text: 'Set up the role and required permissions for your users.',
    progress: 60,
    itemName: 'Role'
  },
  {
    id: 'step-shift',
    route: '/setup/shifts',
    elementId: '#onboarding-add-shift',
    title: 'Create your first shift',
    text: 'Configure the working hours for your employees.',
    progress: 70,
    itemName: 'Shift'
  },
  {
    id: 'step-employee',
    route: '/hr360/employees/list/all',
    elementId: '#onboarding-add-employee',
    title: 'Add your first employee',
    text: 'Finally, now that the required setup is ready, let\'s create your first employee.',
    progress: 80,
    itemName: 'Employee'
  }
];

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  isOnboardingRequired: boolean = false;
  onboardingProgress: number = 0;
  public savingStepIndex: number | null = null;
  public stepChanged$: Subject<number> = new Subject<number>();
  private currentStepIndex: number = 0;
  private isTourActive: boolean = false;

  get steps(): OnboardingStepConfig[] {
    return ONBOARDING_STEPS;
  }

  get currentStep(): number {
    return this.currentStepIndex;
  }

  get completedStepIndex(): number {
    if (this.onboardingProgress >= 100) return ONBOARDING_STEPS.length;
    const index = ONBOARDING_STEPS.findIndex(s => s.progress === this.onboardingProgress);
    return index !== -1 ? index : 0;
  }

  constructor(
    private router: Router,
    private shepherdService: ShepherdService,
    private dataService: DataService,
    private workspaceService: WorkspaceService,
    private toastr: ToastrService
  ) {
    this.setupShepherd();
    this.listenToRouteChanges();
    this.loadWorkspaceState();
    this._checkPostPaymentPrompt();
  }

  /**
   * Check if the user just came from the "Go to Dashboard" button after payment.
   * The payment component sets a localStorage flag before redirecting.
   */
  private _checkPostPaymentPrompt(): void {
    const flag = localStorage.getItem('onboarding_prompt_pending');
    if (flag !== 'true') return;

    localStorage.removeItem('onboarding_prompt_pending');

    // Wait for the first NavigationEnd so the app is fully loaded
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      take(1)
    ).subscribe(() => {
      // Give the page a moment to settle
      setTimeout(() => this.loadWorkspaceStateAndPrompt(), 800);
    });
  }

  private setupShepherd() {
    this.shepherdService.defaultStepOptions = {
      classes: 'shepherd-theme-arrows custom-onboarding-theme shadow-lg',
      scrollTo: true,
      cancelIcon: {
        enabled: true
      }
    };
    this.shepherdService.modal = false;
    this.shepherdService.confirmCancel = false;
  }

  public loadWorkspaceState() {
    const employee = this.dataService.employeeValue;
    if (employee && employee.workspaceId) {
      this.workspaceService.getById(employee.workspaceId).subscribe({
        next: (workspace) => {
          this.isOnboardingRequired = workspace.isOnboardingRequired ?? false;
          this.onboardingProgress = workspace.onboardingProgress ?? 0;
          this.determineCurrentStep();
          
          if (this.isOnboardingRequired && this.isTourActive) {
            this.triggerCurrentStep();
          }
        },
        error: (err) => console.error('Failed to fetch workspace for onboarding', err)
      });
    }
  }

  public refreshWorkspaceState() {
    this.loadWorkspaceState();
  }

  /**
   * Load workspace state then, if onboarding is required, show a prompt
   * asking the user to start or resume the setup tour.
   * Call this from the dashboard ngOnInit.
   */
  public loadWorkspaceStateAndPrompt() {
    const employee = this.dataService.employeeValue;
    if (!employee?.workspaceId) return;

    this.workspaceService.getById(employee.workspaceId).subscribe({
      next: (workspace) => {
        this.isOnboardingRequired = workspace.isOnboardingRequired ?? false;
        this.onboardingProgress = workspace.onboardingProgress ?? 0;
        this.determineCurrentStep();

        if (this.isOnboardingRequired) {
          // Small delay so the dashboard renders first
          setTimeout(() => this.promptAndStart(), 800);
        }
      },
      error: (err) => console.error('Failed to fetch workspace for onboarding', err)
    });
  }

  /**
   * Show a SweetAlert prompt asking the user to start or resume onboarding.
   * If they confirm, the tour begins immediately.
   */
  public async promptAndStart() {
    if (!this.isOnboardingRequired) return;

    const isResume = this.onboardingProgress > 0;
    const stepConfig = ONBOARDING_STEPS[this.currentStepIndex];
    const nextStepName = stepConfig ? stepConfig.itemName : 'the next step';

    const htmlContent = `
      <div class="text-center custom-swal-content">
        <div class="swal-icon-wrapper mb-2">
          <div class="icon-circle">
            <div class="custom-onboarding-svg" style="background-image: url('/assets/images/icons/home/workspace.svg');"></div>
          </div>
        </div>
        <h3 class="fw-bolder mb-1">${isResume ? 'Resume Your Setup' : 'Welcome to OSquare!'}</h3>
        <p class="mb-2">
          ${isResume
            ? `You're <strong>${this.onboardingProgress}%</strong> of the way there!<br>Pick up where you left off and create your <strong>${nextStepName}</strong>.`
            : 'Your workspace is almost ready.<br>Let us guide you through the essential setup steps.'}
        </p>
        <div class="progress-section text-left w-100 mt-2">
          <div class="progress mb-50" style="height: 6px; border-radius: 10px; background-color: #f3f2f7;">
            <div class="progress-bar" role="progressbar" style="width: ${this.onboardingProgress}%; background-color: #7367f0; border-radius: 10px;"></div>
          </div>
          <div class="d-flex justify-content-between" style="font-size: 11px;">
            <span>${this.currentStepIndex + 1} of ${ONBOARDING_STEPS.length} steps</span>
            <span>${this.onboardingProgress}% complete</span>
          </div>
        </div>
      </div>
    `;

    const result = await Swal.fire({
      html: htmlContent,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: `${isResume ? 'Resume Setup' : 'Start Setup'} <i class="feather icon-arrow-right ml-50"></i>`,
      cancelButtonText: 'Maybe Later',
      customClass: {
        popup: 'custom-swal-onboarding',
        confirmButton: 'btn btn-primary swal-btn-custom',
        cancelButton: 'btn btn-outline-secondary swal-btn-custom-outline'
      },
      buttonsStyling: false
    });

    if (result.isConfirmed) {
      this.resume();
    }
  }

  public async handleSaveSuccess(itemName?: string) {
    if (!this.isOnboardingRequired) return;

    // If the tour is NOT active (user saved manually without going through the tour),
    // silently advance progress ONLY if this item matches the current pending step.
    if (!this.isTourActive) {
      const currentStep = ONBOARDING_STEPS[this.currentStepIndex];
      if (currentStep && itemName && currentStep.itemName === itemName) {
        this.advanceProgress(false); // silent — do NOT navigate the user away
      }
      return;
    }

    const currentStep = ONBOARDING_STEPS[this.currentStepIndex];
    const isLastStep = this.currentStepIndex === ONBOARDING_STEPS.length - 1;

    const stepIconMap: {[key: string]: string} = {
      'Country': 'countries',
      'City': 'cities',
      'Company': 'company',
      'Station': 'station',
      'Department': 'department',
      'Designation': 'designation',
      'Role': 'roles',
      'Shift': 'shifts',
      'Employee': 'employees'
    };
    const iconName = stepIconMap[currentStep.itemName] || 'check-circle';
    const totalSteps = ONBOARDING_STEPS.length;
    const currentStepNumber = this.currentStepIndex + 1;
    const nextStep = ONBOARDING_STEPS[this.currentStepIndex + 1];
    const nextStepName = nextStep ? nextStep.itemName.toLowerCase() : 'item';
    
    // Use the predefined progress value for the next step to perfectly match the widget
    const percentage = nextStep ? nextStep.progress : 100;
    const displayStepNumber = Math.min(currentStepNumber + 1, totalSteps);

    if (isLastStep) {
      const htmlContent = `
        <div class="text-center custom-swal-content">
          <div class="swal-icon-wrapper mb-2">
          <div class="icon-circle"><div class="custom-onboarding-svg" style="background-image: url('/assets/images/icons/home/workspace.svg');"></div></div>
          <div class="check-badge">
              <i class="feather icon-check"></i>
            </div>
          </div>
          <h3 class="fw-bolder mb-1">You're All Set!</h3>
          <p class="text-muted mb-2">
            Your workspace is now ready. We've completed the essential setup to get you started.<br>
            Now, explore Osquare and make it yours.
          </p>
          <div class="progress-section text-left w-100 mt-2">
            <div class="progress mb-50" style="height: 6px; border-radius: 10px; background-color: #f3f2f7;">
              <div class="progress-bar" role="progressbar" style="width: 100%; background-color: #7367f0; border-radius: 10px;"></div>
            </div>
            <div class="d-flex justify-content-between text-muted" style="font-size: 11px;">
              <span>${totalSteps} of ${totalSteps} steps</span>
              <span>100% complete</span>
            </div>
          </div>
        </div>
      `;

      await Swal.fire({
        html: htmlContent,
        showCloseButton: true,
        confirmButtonText: 'Explore Osquare <i class="feather icon-arrow-right ml-50"></i>',
        customClass: {
          popup: 'custom-swal-onboarding',
          confirmButton: 'btn btn-primary swal-btn-custom'
        },
        buttonsStyling: false
      });
      this.advanceProgress();
      return;
    }

    const htmlContent = `
      <div class="text-center custom-swal-content">
        <div class="swal-icon-wrapper mb-2">
          <div class="icon-circle"><div class="custom-onboarding-svg" style="background-image: url('/assets/images/icons/home/${iconName}.svg');"></div></div>
          <div class="check-badge">
            <i class="feather icon-check"></i>
          </div>
        </div>
        <h3 class="fw-bolder mb-1">${currentStep.itemName} Saved!</h3>
        <p class="text-muted mb-2">
          You have successfully created your first ${currentStep.itemName.toLowerCase()}.<br>
          Let's proceed to the next step and create a ${nextStepName}.
        </p>
        <div class="progress-section text-left w-100 mt-2">
          <div class="progress mb-50" style="height: 6px; border-radius: 10px; background-color: #f3f2f7;">
            <div class="progress-bar" role="progressbar" style="width: ${percentage}%; background-color: #7367f0; border-radius: 10px;"></div>
          </div>
          <div class="d-flex justify-content-between text-muted" style="font-size: 11px;">
            <span>${displayStepNumber} of ${totalSteps} steps</span>
            <span>${percentage}% complete</span>
          </div>
        </div>
      </div>
    `;

    // Immediately advance progress in the background (no auto-navigate yet)
    // so it persists even if the user closes the alert or logs out.
    this.advanceProgress(false);

    const result = await Swal.fire({
      html: htmlContent,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Next Step <i class="feather icon-arrow-right ml-50"></i>',
      cancelButtonText: 'Skip Setup <i class="feather icon-skip-forward ml-50"></i>',
      customClass: {
        popup: 'custom-swal-onboarding',
        confirmButton: 'btn btn-primary swal-btn-custom',
        cancelButton: 'btn btn-outline-secondary swal-btn-custom-outline'
      },
      buttonsStyling: false
    });

    if (result.value) {
      // User clicked "Next Step"
      if (nextStep) {
        this.router.navigateByUrl(nextStep.route);
      }
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // User clicked "Skip Setup"
      this.skipSetup();
    }
  }

  public advanceProgress(navigateToNext: boolean = true) {
    if (!this.isOnboardingRequired) return;

    this.savingStepIndex = this.currentStepIndex;

    const nextStep = ONBOARDING_STEPS[this.currentStepIndex + 1];
    const newProgress = nextStep ? nextStep.progress : 100;
    const newIsRequired = newProgress < 100;

    const employee = this.dataService.employeeValue;
    if (employee && employee.workspaceId) {
      this.workspaceService.getById(employee.workspaceId).subscribe({
        next: (workspace) => {
          const payload = {
            id: workspace.id,
            name: workspace.name,
            phone: workspace.phone,
            email: workspace.email,
            url: workspace.url,
            employeeLimit: workspace.employeeLimit,
            isOnboardingRequired: newIsRequired,
            onboardingProgress: newProgress
          };

          this.workspaceService.update(payload).subscribe({
            next: () => {
              this.savingStepIndex = null;
              this.refreshWorkspaceState();
              // Only navigate when inside the guided tour
              if (navigateToNext && newIsRequired && nextStep) {
                setTimeout(() => {
                  this.router.navigateByUrl(nextStep.route);
                }, 500);
              }
            },
            error: (err) => {
              console.error('[Onboarding] Failed to update workspace progress', err);
              this.savingStepIndex = null;
              this.refreshWorkspaceState();
            }
          });
        },
        error: (err) => {
          console.error('[Onboarding] Failed to fetch workspace for progress update', err);
          this.savingStepIndex = null;
        }
      });
    } else {
      this.savingStepIndex = null;
    }
  }

  private determineCurrentStep() {
    if (this.onboardingProgress >= 100) {
      this.isOnboardingRequired = false;
      this.currentStepIndex = -1;
      return;
    }

    const index = ONBOARDING_STEPS.findIndex(s => s.progress === this.onboardingProgress);
    if (index !== -1) {
      this.currentStepIndex = index;
    } else {
      this.currentStepIndex = 0;
    }
    this.stepChanged$.next(this.currentStepIndex);
  }

  public resume() {
    if (!this.isOnboardingRequired) return;
    this.isTourActive = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const stepConfig = ONBOARDING_STEPS[this.currentStepIndex];
    if (stepConfig) {
      const currentUrl = this.router.url.split('?')[0];
      if (currentUrl.includes(stepConfig.route)) {
        this.triggerCurrentStep();
      } else {
        this.router.navigateByUrl(stepConfig.route);
      }
    }
  }

  public skipSetup() {
    this.isTourActive = false;
    this.shepherdService.cancel();
  }

  private _loginPromptShown = false;

  private listenToRouteChanges() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url: string = event.urlAfterRedirects ?? '';

        // ── Post-login trigger ──────────────────────────────────────────
        // The index component always appends ?login=true when redirecting
        // after a fresh login. Detect this once and show the onboarding prompt.
        if (!this._loginPromptShown && url.includes('login=true')) {
          this._loginPromptShown = true;
          setTimeout(() => this.loadWorkspaceStateAndPrompt(), 800);
          return;
        }

        // ── Active tour step navigation ─────────────────────────────────
        if (!this.isOnboardingRequired || !this.isTourActive) return;

        const currentUrl = url.split('?')[0];
        const stepConfig = ONBOARDING_STEPS[this.currentStepIndex];

        if (stepConfig && currentUrl.includes(stepConfig.route)) {
          this.showShepherdStep(stepConfig);
        } else {
          // User navigated away from the tour step, cancel the tour
          this.isTourActive = false;
          this.shepherdService.cancel();
        }
      });
  }

  private triggerCurrentStep() {
    if (!this.isTourActive) return;
    const stepConfig = ONBOARDING_STEPS[this.currentStepIndex];
    const currentUrl = this.router.url.split('?')[0];

    if (stepConfig && currentUrl.includes(stepConfig.route)) {
      this.showShepherdStep(stepConfig);
    }
  }

  private showShepherdStep(stepConfig: OnboardingStepConfig) {
    if (this.shepherdService.isActive) {
      this.shepherdService.cancel();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.stepChanged$.next(this.currentStepIndex);

    const tryShow = (attempts: number) => {
      const element = document.querySelector(stepConfig.elementId);
      if (element) {
        // this.toastr.success(`Found element ${stepConfig.elementId}`);
        try {
          this.createAndShowStep(stepConfig);
        } catch(e: any) {
          this.toastr.error('Shepherd error: ' + e.message);
        }
      } else if (attempts > 0) {
        setTimeout(() => tryShow(attempts - 1), 500);
      } else {
        this.toastr.error(`Could not find ${stepConfig.elementId}`);
      }
    };

    tryShow(10);
  }

  private createAndShowStep(stepConfig: OnboardingStepConfig) {
    if (this.shepherdService.tourObject) {
       const existingSteps = [...this.shepherdService.tourObject.steps];
       existingSteps.forEach(step => this.shepherdService.tourObject.removeStep(step.id));
    }

    this.shepherdService.addSteps([
      {
        id: stepConfig.id,
        title: '',
        text: `
          <div class="d-flex align-items-start text-left">
            <div class="tour-step-badge mr-1" style="margin-right: 12px;">${this.currentStepIndex + 1} <span style="font-size: 8px; opacity: 0.8; font-weight: normal; margin: 0 2px;">/</span> ${ONBOARDING_STEPS.length}</div>
            <div style="flex: 1;">
              <h5 class="fw-bolder mb-50" style="color: #1E1E2D; font-size: 14px; margin-top: 6px;">${stepConfig.title}</h5>
              <p class="mb-0" style="font-size: 12px; line-height: 1.6; color: #302b2b !important">${stepConfig.text}</p>
            </div>
          </div>
        `,
        attachTo: {
          element: stepConfig.elementId,
          on: 'left' // Adjust to left to match the arrow in the screenshot
        },
        advanceOn: {
          selector: stepConfig.elementId,
          event: 'click'
        },
        buttons: [
          {
            classes: 'shepherd-button-secondary',
            text: 'Skip Setup',
            action: () => this.skipSetup()
          },
          {
            classes: 'shepherd-button-primary',
            text: 'Next &rarr;',
            action: () => {
               this.shepherdService.cancel();
               this.advanceProgress();
            }
          }
        ],
        when: {
          cancel: () => {
            this.isTourActive = false;
          }
        }
      }
    ]);
    this.shepherdService.start();
  }
}
